/**
 * Video Reels
 * Instagram/TikTok-style horizontal reels rail for Horizon.
 *
 * Behaviour:
 *  - Every visible reel autoplays muted (autoplay policy safe).
 *  - Reels pause only when completely outside the viewport (and on tab hide).
 *  - Videos are lazy loaded and only fetched when near the viewport.
 *  - The reel nearest the rail centre is marked `.is-active` (centred + scaled).
 *  - Smooth, snap horizontal scrolling with wrap-around arrows for an infinite feel.
 *  - Clicking a reel opens a popup player with product info + a shop button.
 *
 * No polling, no timers on the hot path. Scroll work is throttled with
 * requestAnimationFrame. Two IntersectionObservers do the heavy lifting.
 */
class VideoReels extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-reels-track]');
    this.cards = Array.from(this.querySelectorAll('[data-reel]'));
    if (!this.track || this.cards.length === 0) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.visible = new Set();
    this.activeCard = null;
    this._raf = 0;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    this.#setupLazyLoad();
    this.#setupPlayback();

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);

    this.#setupArrows();
    this.#setupModal();
    this.updateActive();
  }

  disconnectedCallback() {
    this.loadObserver?.disconnect();
    this.playObserver?.disconnect();
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
  }

  /* -------- Lazy loading: only fetch videos near the viewport -------- */
  #setupLazyLoad() {
    this.loadObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#loadVideo(entry.target);
            this.loadObserver.unobserve(entry.target);
          }
        }
      },
      { root: null, rootMargin: '200% 0px 200% 0px', threshold: 0 }
    );
    this.cards.forEach((card) => this.loadObserver.observe(card));
  }

  #loadVideo(card) {
    const video = card.querySelector('video');
    if (!video || video.dataset.loaded === 'true') return;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    if (video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
    }
    video.dataset.loaded = 'true';
    video.load();
  }

  /* -------- Playback: play visible, pause fully offscreen -------- */
  #setupPlayback() {
    this.playObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const card = entry.target;
          if (entry.isIntersecting) {
            this.visible.add(card);
            this.#loadVideo(card);
            if (!this.modalOpen) this.#play(card.querySelector('video'));
          } else {
            this.visible.delete(card);
            card.querySelector('video')?.pause();
          }
        }
      },
      // Any sliver visible => intersecting => keep playing.
      { root: null, rootMargin: '0px', threshold: 0.01 }
    );
    this.cards.forEach((card) => this.playObserver.observe(card));
  }

  #play(video) {
    if (!video) return;
    video.muted = true; // required for autoplay
    const attempt = video.play();
    // Swallow AbortError/NotAllowedError so reels never "stop" with console noise.
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        // Rejected because data wasn't ready yet: retry once it can play,
        // but only if the reel is still visible. Guarantees "always play while visible".
        const card = video.closest('[data-reel]');
        video.addEventListener(
          'canplay',
          () => {
            if (this.visible.has(card) && !this.modalOpen) {
              const retry = video.play();
              if (retry && typeof retry.catch === 'function') retry.catch(() => {});
            }
          },
          { once: true }
        );
      });
    }
  }

  #pauseAllVisible() {
    this.visible.forEach((card) => card.querySelector('video')?.pause());
  }

  #resumeAllVisible() {
    if (document.hidden || this.modalOpen) return;
    this.visible.forEach((card) => this.#play(card.querySelector('video')));
  }

  /* -------- Active (centred) reel tracking -------- */
  onScroll() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = 0;
      this.updateActive();
    });
  }

  onResize() {
    // Debounce to a single frame to avoid layout thrash on resize.
    if (this._resizeRaf) return;
    this._resizeRaf = requestAnimationFrame(() => {
      this._resizeRaf = 0;
      this.updateActive();
    });
  }

  updateActive() {
    const rect = this.track.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    let best = null;
    let bestDist = Infinity;
    for (const card of this.cards) {
      const r = card.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - center);
      if (dist <= bestDist) {
        bestDist = dist;
        best = card;
      } else {
        // Cards are laid out left-to-right, so distance is convex: once it
        // starts growing we have passed the centre. Stops scanning every card.
        break;
      }
    }
    if (best && best !== this.activeCard) {
      this.activeCard?.classList.remove('is-active');
      best.classList.add('is-active');
      this.activeCard = best;
    }
  }

  /* -------- Tab visibility: resume the reels that were playing -------- */
  onVisibility() {
    if (document.hidden) {
      this.cards.forEach((card) => card.querySelector('video')?.pause());
    } else {
      this.#resumeAllVisible();
    }
  }

  /* -------- Wrap-around arrow navigation ("infinite feeling") -------- */
  #setupArrows() {
    const prev = this.querySelector('[data-reels-prev]');
    const next = this.querySelector('[data-reels-next]');
    prev?.addEventListener('click', () => this.#scrollByCard(-1));
    next?.addEventListener('click', () => this.#scrollByCard(1));
  }

  #scrollByCard(direction) {
    const card = this.cards[0];
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = card.getBoundingClientRect().width + gap;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    let target = this.track.scrollLeft + direction * step;

    if (direction > 0 && this.track.scrollLeft >= maxScroll - 2) {
      target = 0; // reached the end -> loop to start
    } else if (direction < 0 && this.track.scrollLeft <= 2) {
      target = maxScroll; // at the start -> loop to end
    }

    this.track.scrollTo({ left: target, behavior: this.reduceMotion ? 'auto' : 'smooth' });
  }

  /* -------- Popup player -------- */
  #setupModal() {
    this.modal = this.querySelector('[data-reels-modal]');
    if (!this.modal) return;
    this.modalVideo = this.modal.querySelector('[data-reels-modal-video]');
    this.modalOpen = false;

    // Open when a reel's media button is activated.
    this.querySelectorAll('[data-reel-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.#openModal(btn.closest('[data-reel]')));
    });

    // Close controls: button, backdrop click, ESC (native <dialog> handles ESC).
    this.modal.querySelector('[data-reels-close]')?.addEventListener('click', () => this.#closeModal());
    this.modal.addEventListener('click', (event) => {
      // Click on the dialog element itself (the backdrop area) closes it.
      if (event.target === this.modal) this.#closeModal();
    });
    this.modal.addEventListener('close', () => this.#onModalClosed());

    // Mute / unmute toggle inside the popup.
    this.soundBtn = this.modal.querySelector('[data-reels-sound]');
    this.soundBtn?.addEventListener('click', () => {
      if (!this.modalVideo) return;
      this.modalVideo.muted = !this.modalVideo.muted;
      this.modal.classList.toggle('is-muted', this.modalVideo.muted);
    });
  }

  #openModal(card) {
    if (!this.modal || !card || !this.modalVideo) return;
    const src = card.dataset.videoSrc;
    if (!src) return;

    this.modalOpen = true;
    this.#pauseAllVisible();

    // Populate video.
    if (card.dataset.poster) this.modalVideo.poster = card.dataset.poster;
    this.modalVideo.src = src;
    this.modalVideo.muted = true;
    this.modal.classList.add('is-muted');
    this.modalVideo.load();

    // Populate product info.
    const info = this.modal.querySelector('[data-reels-modal-info]');
    const titleEl = this.modal.querySelector('[data-reels-modal-title]');
    const priceEl = this.modal.querySelector('[data-reels-modal-price]');
    const shopEl = this.modal.querySelector('[data-reels-modal-shop]');
    const title = card.dataset.title || '';
    const price = card.dataset.price || '';
    const url = card.dataset.productUrl || '';

    if (titleEl) titleEl.textContent = title;
    if (priceEl) {
      priceEl.textContent = price;
      priceEl.hidden = price === '';
    }
    if (shopEl) {
      if (url) {
        shopEl.href = url;
        shopEl.hidden = false;
      } else {
        shopEl.hidden = true;
      }
    }
    if (info) info.hidden = title === '' && price === '' && url === '';

    if (typeof this.modal.showModal === 'function') {
      this.modal.showModal();
    } else {
      this.modal.setAttribute('open', '');
    }

    const attempt = this.modalVideo.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }

  #closeModal() {
    if (!this.modal) return;
    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
      this.#onModalClosed();
    }
  }

  #onModalClosed() {
    this.modalOpen = false;
    if (this.modalVideo) {
      this.modalVideo.pause();
      this.modalVideo.removeAttribute('src');
      this.modalVideo.load(); // release the buffer
    }
    this.#resumeAllVisible();
  }
}

if (!customElements.get('video-reels')) {
  customElements.define('video-reels', VideoReels);
}
